import {
    useContext,
    useEffect,
    type FC,
    useRef
} from "react";
import {
    type LayoutChangeEvent,
    Pressable,
    Platform,
    View
} from "react-native";
import type ITabsTriggerProps from "./type";
import type {
    TabsTriggerKeyboardEventType,
    TabsTriggerWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    TabsPartContentProps
} from "../../type";
import {
    TABS_NAVIGATION_KEYS,
    TABS_SPACE_KEY,
    getTabsID
} from "../../util";
import {
    splitControlStyle
} from "../../../../helpers/theme/control";
import {
    TabsListContext,
    withTabsContext
} from "../../context";
import {
    warnOnce
} from "../../../../utils/development";
import rootStylesheet, {
    getTabsMarkStyle,
    getTabsMeasures,
    getTabsColors
} from "../../stylesheet";
import TabsCounter from "../../internal/counter";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const TabsTriggerContent: FC<TabsPartContentProps<ITabsTriggerProps>> = ({
    customLocalize: customLocalizeProp,
    isDisabled: isDisabledProp = false,
    customTheme: customThemeProp,
    "aria-label": ariaLabel,
    onLayout: onLayoutProp,
    countType = "neutral",
    onFocus: onFocusProp,
    accessibilityLabel,
    icon: IconProp,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    context,
    onBlur,
    count,
    style,
    title,
    value,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const customLocalize = customLocalizeProp ?? context.customLocalize;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const listContext = useContext(TabsListContext);

    const pointerType = usePointerType();

    const triggerRef = useRef<View>(null);

    const isSelected = context.value === value;

    const isDisabled = context.isDisabled || isDisabledProp;

    const isRadio = context.listRole === "radiogroup";

    const isTabStop = context.tabStopValue === undefined ? isSelected : context.tabStopValue === value;

    const isIconOnly = !!IconProp && !title;

    const isHidden = listContext?.hiddenValues.includes(value) ?? false;

    const onFocus: NonNullable<ITabsTriggerProps["onFocus"]> = (event) => {
        listContext?.handleReveal(value);

        onFocusProp?.(event);
    };

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const currentMeasures = getTabsMeasures({
        orientation: context.orientation,
        variant: context.variant,
        size: context.size,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        iconSizes,
        radiuses,
        spaces
    });

    const isMarkVisible = isSelected && listContext?.slide?.to !== value;

    const isHoverVisible = isHovered && !isSelected;

    const isPressVisible = isPressed && !isSelected;

    const currentColors = getTabsColors({
        isHighlighted: isHoverVisible || isPressVisible,
        isDisabled,
        isSelected,
        colors
    });

    const markStyle = getTabsMarkStyle({
        orientation: context.orientation,
        measures: currentMeasures,
        variant: context.variant,
        shapeConfigs,
        borders,
        colors
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const {
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle,
        label: labelDynamicStyle,
        layer: layerDynamicStyle
    } = useStyles({
        spreadBehaviour: context.spreadBehaviour,
        isFixed: listContext?.isFixed ?? false,
        orientation: context.orientation,
        variant: context.variant,
        currentMeasures,
        isFocusVisible,
        isMarkVisible,
        shapeConfigs,
        isHidden,
        radiuses,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        return context.registerTrigger(value, {
            ref: triggerRef,
            isDisabled,
            isHidden
        });
    }, [
        context.registerTrigger,
        isDisabled,
        isHidden,
        value
    ]);

    useEffect(() => {
        if(listContext) {
            return;
        }

        warnOnce({
            message: "Tabs.Trigger: must be rendered inside <Tabs.List>."
        });
    }, [
        listContext
    ]);

    useEffect(() => {
        if(!isIconOnly || ariaLabel || accessibilityLabel) {
            return;
        }

        warnOnce({
            message: "Tabs.Trigger: an icon-only trigger needs an \"accessibilityLabel\" (or \"aria-label\") so screen readers can announce it."
        });
    }, [
        accessibilityLabel,
        isIconOnly,
        ariaLabel
    ]);

    const handlePress = () => {
        context.handleSelect(value);
    };

    const onLayout = (event: LayoutChangeEvent) => {
        listContext?.handleLayout(value, event.nativeEvent.layout);

        onLayoutProp?.(event);
    };

    const onKeyDown = (event: TabsTriggerKeyboardEventType) => {
        if(event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        if(event.key === TABS_SPACE_KEY) {
            event.preventDefault();

            return;
        }

        if(!TABS_NAVIGATION_KEYS.includes(event.key)) {
            return;
        }

        if(context.handleNavigate({
            key: event.key,
            value
        })) {
            event.preventDefault();
        }
    };

    const onKeyUp = (event: TabsTriggerKeyboardEventType) => {
        if(event.key !== TABS_SPACE_KEY || event.target !== event.currentTarget || isDisabled) {
            return;
        }

        event.preventDefault();

        handlePress();
    };

    const webProps: TabsTriggerWebPropsType = Platform.OS === "web" ? {
        "aria-controls": isRadio ? undefined : getTabsID({
            baseID: context.baseID,
            kind: "content",
            value
        }),
        id: getTabsID({
            baseID: context.baseID,
            kind: "trigger",
            value
        }),
        onKeyDown,
        onKeyUp
    } : {};

    const renderLayer = () => {
        if(context.variant === "segment") {
            return <StateLayer
                borderRadius={currentMeasures.itemRadius}
                isHovered={isHoverVisible}
                isPressed={isPressVisible}
                customTheme={customTheme}
                overlayColor="default"
                isSquircle={true}
            />;
        }

        return <View
            style={[
                stylesheet.layer,
                layerDynamicStyle
            ]}
            aria-hidden={true}
        >
            <StateLayer
                borderRadius={radiuses.actions}
                isHovered={isHoverVisible}
                isPressed={isPressVisible}
                customTheme={customTheme}
                overlayColor="default"
                isSquircle={true}
            />
        </View>;
    };

    const renderMark = () => {
        if(context.variant !== "segment" || !isMarkVisible) {
            return null;
        }

        return <View
            style={[
                rootStylesheet.ring,
                markStyle
            ]}
            aria-hidden={true}
        />;
    };

    const renderIcon = () => {
        if(!IconProp) {
            return null;
        }

        return <View
            style={stylesheet.icon}
            aria-hidden={true}
        >
            <IconProp
                customColor={currentColors.iconColor}
                color={currentColors.iconColorKey}
                size={currentMeasures.iconSize}
            />
        </View>;
    };

    const renderTitle = () => {
        if(!title) {
            return null;
        }

        return <Text
            variant={currentMeasures.labelVariant}
            customColor={currentColors.titleColor}
            customTheme={customTheme}
            style={[
                stylesheet.label,
                labelDynamicStyle
            ]}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    const renderCounter = () => {
        if(count === undefined) {
            return null;
        }

        return <TabsCounter
            customLocalize={customLocalize}
            variant={context.variant}
            customTheme={customTheme}
            isDisabled={isDisabled}
            isSelected={isSelected}
            countType={countType}
            size={context.size}
            count={count}
        />;
    };

    if(!listContext) {
        return null;
    }

    return <Pressable
        {...props}
        {...webProps}
        {...interactionHandlers}
        tabIndex={isTabStop && !isDisabled && !isHidden ? 0 : -1}
        aria-selected={isRadio ? undefined : isSelected}
        aria-checked={isRadio ? isSelected : undefined}
        aria-hidden={isHidden ? true : undefined}
        accessibilityLabel={accessibilityLabel}
        role={isRadio ? "radio" : "tab"}
        aria-disabled={isDisabled}
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-label={ariaLabel}
        onPress={handlePress}
        disabled={isDisabled}
        onLayout={onLayout}
        ref={triggerRef}
    >
        <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            {renderLayer()}
            {renderMark()}
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderIcon()}
                {renderTitle()}
                {renderCounter()}
            </View>
        </View>
    </Pressable>;
};
export default withTabsContext("trigger", TabsTriggerContent);
